import { useState } from "react";
import axios from "axios";
import {
  Mail,
  MapPin,
  MessageCircle,
  Instagram,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Reveal, SectionHead, TikTokIcon } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Website Development",
  "IT Support & Solutions",
  "Something else",
];

const CONTACT = [
  { icon: Mail, label: "Email", value: "info@btdesign.co.za", href: "mailto:info@btdesign.co.za", testid: "contact-info-email" },
  { icon: MessageCircle, label: "WhatsApp", value: "+27 67 563 5275", href: "https://wa.me/message/GRQ25AXPDY2EI1", testid: "contact-info-whatsapp" },
  { icon: Instagram, label: "Instagram", value: "@btdesign.digital", href: "https://www.instagram.com/btdesign.digital", testid: "contact-info-instagram" },
  { icon: TikTokIcon, label: "TikTok", value: "BT Design_Digital", href: "https://www.tiktok.com/@auvixtech", testid: "contact-info-tiktok" },
  { icon: MapPin, label: "Location", value: "South Africa · Serving clients nationwide", href: null, testid: "contact-info-location" },
];

const EMPTY = { name: "", email: "", phone: "", company: "", service: SERVICE_OPTIONS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [refId, setRefId] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (form.message.trim().length < 10) {
      setError("Please tell us a little more (at least 10 characters).");
      return;
    }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      setRefId(res.data.id);
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
      setError("Something went wrong sending your enquiry. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-20 py-24 md:py-32">
      <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
        <div>
          <SectionHead
            eyebrow="Get In Touch"
            title="Tell us what you need built or fixed."
            sub="Send the form and we will come back to you with clear next steps, a rough timeline and an honest answer about whether we are the right fit."
          />

          <div className="mt-12 flex flex-col gap-6">
            {CONTACT.map(({ icon: Icon, label, value, href, testid }, i) => (
              <Reveal key={label} delay={i * 0.08} data-testid={testid}>
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border hairline bg-card">
                    <Icon className="h-5 w-5 text-cy" strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub/70">
                      {label}
                    </p>
                    {href ? (
                      <a
                        href={href}
                        target={href.startsWith("https") ? "_blank" : undefined}
                        rel={href.startsWith("https") ? "noopener noreferrer" : undefined}
                        className="mt-1 block text-sm text-txt transition-colors duration-300 hover:text-cy"
                      >
                        {value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-txt">{value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border hairline bg-card p-8 md:p-10">
            {status === "success" ? (
              <div
                data-testid="enquiry-success-message"
                className="flex h-full min-h-[420px] flex-col items-center justify-center text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue to-cy">
                  <CheckCircle2 className="h-8 w-8 text-ink" strokeWidth={2.2} />
                </span>
                <h3 className="mt-7 font-grotesk text-2xl font-semibold tracking-tight text-txt">
                  Enquiry received. Thank you.
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-sub">
                  Your message is safely with us and we will get back to you
                  within one business day.
                </p>
                {refId && (
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-sub/60">
                    Reference · {refId.slice(-8).toUpperCase()}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="btn-ghost mt-9"
                  data-testid="enquiry-reset-button"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" noValidate>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                      Name *
                    </span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      data-testid="enquiry-input-name"
                      placeholder="Your full name"
                      className="field mt-2.5"
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                      Email *
                    </span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      data-testid="enquiry-input-email"
                      placeholder="you@company.co.za"
                      className="field mt-2.5"
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                      Phone
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      data-testid="enquiry-input-phone"
                      placeholder="+27 ..."
                      className="field mt-2.5"
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                      Company
                    </span>
                    <input
                      type="text"
                      value={form.company}
                      onChange={set("company")}
                      data-testid="enquiry-input-company"
                      placeholder="Business or organisation"
                      className="field mt-2.5"
                    />
                  </label>
                </div>

                <label className="relative mt-5 block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                    What do you need?
                  </span>
                  <select
                    value={form.service}
                    onChange={set("service")}
                    data-testid="enquiry-select-service"
                    className="field mt-2.5 appearance-none pr-10"
                  >
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-ink2 text-txt">
                        {o}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute bottom-3.5 right-4 h-4 w-4 text-sub"
                    aria-hidden="true"
                  />
                </label>

                <label className="mt-5 block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">
                    Message *
                  </span>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="enquiry-input-message"
                    placeholder="Tell us about your project, problem or idea..."
                    className="field mt-2.5 resize-none"
                  />
                </label>

                {error && (
                  <p
                    data-testid="enquiry-error-message"
                    className="mt-4 flex items-center gap-2 text-sm text-red-400"
                    role="alert"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="enquiry-submit-button"
                  className="btn-primary mt-7 w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "sending" ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
